import { Coffee, TrendingUp, Clock, CheckCircle, ArrowRight, Calculator, DollarSign, Users, Home } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

const LOGO_URL = "https://img1.wsimg.com/isteam/ip/d9f026ae-fdac-4ce0-8e76-8f2f65f8f36d/CafeSales%20Logo.png/:/rs=h:139,cg:true,m/qt=q:95";

const LandingScreenAlt = ({ onStart }) => {
    const steps = [
        { icon: Coffee, title: "Enter Your Details", desc: "Answer 12 simple questions about your café" },
        { icon: Calculator, title: "Get Your Valuation", desc: "Our algorithm calculates your business value" },
        { icon: DollarSign, title: "Know Your Worth", desc: "Receive a detailed valuation report" }
    ];

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900">
            {/* Header */}
            <header className="w-full py-6 px-6 lg:px-12">
                <div className="max-w-6xl mx-auto flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Link 
                            to="/" 
                            className="flex items-center gap-2 px-3 py-2 rounded-lg text-purple-200 hover:text-white hover:bg-white/10 transition-colors"
                            data-testid="back-home-link"
                        >
                            <Home className="w-4 h-4" />
                            <span className="text-sm font-medium hidden sm:inline">Home</span>
                        </Link>
                        <img 
                            src={LOGO_URL} 
                            alt="Cafe Sales Experts" 
                            className="h-12 md:h-16 object-contain brightness-0 invert"
                            data-testid="cafe-sales-logo"
                        />
                    </div>
                    <div className="hidden md:flex items-center gap-2 text-sm text-purple-200">
                        <CheckCircle className="w-4 h-4 text-green-400" />
                        <span>Free Instant Valuation</span>
                    </div>
                </div>
            </header>

            {/* Hero Section */}
            <main className="max-w-6xl mx-auto px-6 lg:px-12 pt-8 pb-20">
                <div className="text-center mb-16">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 text-purple-200 text-sm font-medium mb-6 backdrop-blur-sm">
                            <Calculator className="w-4 h-4" />
                            <span>Café Valuation Calculator</span>
                        </div>
                        
                        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
                            Discover What Your
                            <br />
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-pink-500">
                                Café Is Really Worth
                            </span>
                        </h1>
                        
                        <p className="text-lg text-purple-200 max-w-2xl mx-auto mb-8">
                            Get a free, instant valuation based on real café sales data and 
                            industry benchmarks. No obligation, takes under 3 minutes.
                        </p>

                        <Button
                            onClick={onStart}
                            size="lg"
                            className="bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white shadow-2xl shadow-orange-500/30 transition-all duration-300 transform hover:-translate-y-1 hover:scale-105 rounded-full px-10 py-7 text-lg font-semibold group"
                            data-testid="start-valuation-btn"
                        >
                            Get My Free Valuation
                            <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                        </Button>
                    </motion.div>
                </div>

                {/* How It Works */}
                <motion.div
                    className="grid md:grid-cols-3 gap-8 mb-16"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                >
                    {steps.map((step, index) => (
                        <div 
                            key={index}
                            className="relative bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-orange-500/50 transition-all duration-300 group"
                        >
                            <div className="absolute -top-4 -left-4 w-10 h-10 bg-gradient-to-r from-orange-500 to-pink-500 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg">
                                {index + 1}
                            </div>
                            <div className="w-14 h-14 rounded-xl bg-white/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                                <step.icon className="w-7 h-7 text-orange-400" />
                            </div>
                            <h3 className="text-xl font-semibold text-white mb-2">{step.title}</h3>
                            <p className="text-purple-200">{step.desc}</p>
                        </div>
                    ))}
                </motion.div>

                {/* Stats */}
                <motion.div
                    className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.4 }}
                >
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
                        <div>
                            <p className="text-3xl md:text-4xl font-bold text-white mb-1">900+</p>
                            <p className="text-sm text-purple-300">Cafés Sold</p>
                        </div>
                        <div>
                            <p className="text-3xl md:text-4xl font-bold text-white mb-1">$10M+</p>
                            <p className="text-sm text-purple-300">Commissions Saved</p>
                        </div>
                        <div>
                            <p className="text-3xl md:text-4xl font-bold text-white mb-1">4%</p>
                            <p className="text-sm text-purple-300">Fair Commission</p>
                        </div>
                        <div>
                            <p className="text-3xl md:text-4xl font-bold text-white mb-1">2,000+</p>
                            <p className="text-sm text-purple-300">Valuations Done</p>
                        </div>
                    </div>
                </motion.div>

                {/* Trust Badges */}
                <motion.div
                    className="flex flex-wrap items-center justify-center gap-6 mt-12 text-purple-300 text-sm"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.6, delay: 0.6 }}
                >
                    <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-400" />
                        <span>100% Free</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-400" />
                        <span>No Obligation</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-400" />
                        <span>Instant Results</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-400" />
                        <span>Based on Real Sales</span>
                    </div>
                </motion.div>
            </main>

            {/* Footer */}
            <footer className="w-full py-6 px-6 border-t border-white/10">
                <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-purple-300">
                    <p>© 2026 Cafe Sales Experts</p>
                    <Link 
                        to="/"
                        className="hover:text-orange-400 transition-colors"
                    >
                        Back to Home
                    </Link>
                </div>
            </footer>
        </div>
    );
};

export default LandingScreenAlt;
